import { Type } from "@scalar/typebox";
import { compose } from "../../../schemas/compose.js";
import { extensions } from "../../../schemas/extensions.js";
import { TraversedEntrySchema } from "../../../schemas/navigation.js";
import { CallbackObjectSchemaDefinition } from "../../../schemas/v3.1/strict/callback.js";
import { xScalarClientConfigCookiesSchema } from "../../../schemas/v3.1/strict/client-config-extensions/x-scalar-client-config-cookies.js";
import { xScalarClientConfigEnvironmentsSchema } from "../../../schemas/v3.1/strict/client-config-extensions/x-scalar-client-config-environments.js";
import { ContactObjectSchemaDefinition } from "../../../schemas/v3.1/strict/contact.js";
import { DiscriminatorObjectSchemaDefinition } from "../../../schemas/v3.1/strict/discriminator.js";
import { EncodingObjectSchemaDefinition } from "../../../schemas/v3.1/strict/encoding.js";
import { ExampleObjectSchemaDefinition } from "../../../schemas/v3.1/strict/example.js";
import { HeaderObjectSchemaDefinition } from "../../../schemas/v3.1/strict/header.js";
import { LicenseObjectSchemaDefinition } from "../../../schemas/v3.1/strict/license.js";
import { LinkObjectSchemaDefinition } from "../../../schemas/v3.1/strict/link.js";
import { MediaTypeObjectSchemaDefinition } from "../../../schemas/v3.1/strict/media-type.js";
import { OAuthFlowsObjectSchemaDefinition } from "../../../schemas/v3.1/strict/oauthflows.js";
import { OperationObjectSchemaDefinition } from "../../../schemas/v3.1/strict/operation.js";
import { ParameterObjectSchemaDefinition } from "../../../schemas/v3.1/strict/parameter.js";
import { PathItemObjectSchemaDefinition } from "../../../schemas/v3.1/strict/path-item.js";
import {
  ComponentsObjectRef,
  ExternalDocumentationObjectRef,
  InfoObjectRef,
  PathItemObjectRef,
  PathsObjectRef,
  REF_DEFINITIONS,
  SecurityRequirementObjectRef,
  ServerObjectRef,
  TagObjectRef
} from "../../../schemas/v3.1/strict/ref-definitions.js";
import { RequestBodyObjectSchemaDefinition } from "../../../schemas/v3.1/strict/request-body.js";
import { ResponseObjectSchemaDefinition } from "../../../schemas/v3.1/strict/response.js";
import { ResponsesObjectSchemaDefinition } from "../../../schemas/v3.1/strict/responses.js";
import { SchemaObjectSchemaDefinition } from "../../../schemas/v3.1/strict/schema.js";
import { SecuritySchemeObjectSchemaDefinition } from "../../../schemas/v3.1/strict/security-scheme.js";
import { ServerVariableObjectSchemaDefinition } from "../../../schemas/v3.1/strict/server-variable.js";
import { XMLObjectSchemaDefinition } from "../../../schemas/v3.1/strict/xml.js";
import { ComponentsObjectSchemaDefinition } from "./components.js";
import { ExternalDocumentationObjectSchemaDefinition } from "./external-documentation.js";
import { InfoObjectSchemaDefinition } from "./info.js";
import { PathsObjectSchemaDefinition } from "./paths.js";
import { SecurityRequirementObjectSchemaDefinition } from "./security-requirement.js";
import { ServerObjectSchemaDefinition } from "./server.js";
import { TagObjectSchemaDefinition } from "./tag.js";
const OpenApiExtensionsSchema = Type.Partial(
  Type.Object({
    "x-tagGroups": Type.Array(
      compose(
        Type.Object({
          tags: Type.Array(Type.String())
        }),
        TagObjectRef
      )
    ),
    "x-scalar-client-config-active-environment": Type.String(),
    /** A custom icon representing the collection */
    "x-scalar-client-config-icon": Type.String(),
    "x-scalar-client-config-environments": xScalarClientConfigEnvironmentsSchema,
    "x-scalar-client-config-cookies": xScalarClientConfigCookiesSchema,
    "x-original-oas-version": Type.String(),
    [extensions.document.navigation]: Type.Array(TraversedEntrySchema)
  })
);
const OpenApiDocumentSchemaDefinition = compose(
  Type.Object({
    /** REQUIRED. This string MUST be the version number of the OpenAPI Specification that the OpenAPI Document uses. The openapi field SHOULD be used by tooling to interpret the OpenAPI Document. This is not related to the API info.version string. */
    openapi: Type.String(),
    /** REQUIRED. Provides metadata about the API. The metadata MAY be used by tooling as required. */
    info: InfoObjectRef,
    /** The default value for the $schema keyword within Schema Objects contained within this OAS document. This MUST be in the form of a URI. */
    jsonSchemaDialect: Type.Optional(Type.String()),
    /** An array of Server Objects, which provide connectivity information to a target server. If the servers field is not provided, or is an empty array, the default value would be a Server Object with a url value of /. */
    servers: Type.Optional(Type.Array(ServerObjectRef)),
    /** The available paths and operations for the API. */
    paths: Type.Optional(PathsObjectRef),
    /** The incoming webhooks that MAY be received as part of this API and that the API consumer MAY choose to implement. Closely related to the callbacks feature, this section describes requests initiated other than by an API call, for example by an out of band registration. The key name is a unique string to refer to each webhook, while the (optionally referenced) Path Item Object describes a request that may be initiated by the API provider and the expected responses. An example is available. */
    webhooks: Type.Optional(Type.Record(Type.String(), PathItemObjectRef)),
    /** An element to hold various Objects for the OpenAPI Description. */
    components: Type.Optional(ComponentsObjectRef),
    /** A declaration of which security mechanisms can be used across the API. The list of values includes alternative Security Requirement Objects that can be used. Only one of the Security Requirement Objects need to be satisfied to authorize a request. Individual operations can override this definition. The list can be incomplete, up to being empty or absent. To make security explicitly optional, an empty security requirement ({}) can be included in the array. */
    security: Type.Optional(Type.Array(SecurityRequirementObjectRef)),
    /** A list of tags used by the OpenAPI Description with additional metadata. The order of the tags can be used to reflect on their order by the parsing tools. Not all tags that are used by the Operation Object must be declared. The tags that are not declared MAY be organized randomly or based on the tools' logic. Each tag name in the list MUST be unique. */
    tags: Type.Optional(Type.Array(TagObjectRef)),
    /** Additional external documentation. */
    externalDocs: Type.Optional(ExternalDocumentationObjectRef)
  }),
  OpenApiExtensionsSchema
);
const module = Type.Module({
  [REF_DEFINITIONS.ComponentsObject]: ComponentsObjectSchemaDefinition,
  [REF_DEFINITIONS.SecurityRequirementObject]: SecurityRequirementObjectSchemaDefinition,
  [REF_DEFINITIONS.TagObject]: TagObjectSchemaDefinition,
  [REF_DEFINITIONS.CallbackObject]: CallbackObjectSchemaDefinition,
  [REF_DEFINITIONS.PathItemObject]: PathItemObjectSchemaDefinition,
  [REF_DEFINITIONS.PathsObject]: PathsObjectSchemaDefinition,
  [REF_DEFINITIONS.OperationObject]: OperationObjectSchemaDefinition,
  [REF_DEFINITIONS.SchemaObject]: SchemaObjectSchemaDefinition,
  [REF_DEFINITIONS.EncodingObject]: EncodingObjectSchemaDefinition,
  [REF_DEFINITIONS.MediaTypeObject]: MediaTypeObjectSchemaDefinition,
  [REF_DEFINITIONS.HeaderObject]: HeaderObjectSchemaDefinition,
  [REF_DEFINITIONS.ServerObject]: ServerObjectSchemaDefinition,
  [REF_DEFINITIONS.ExternalDocumentationObject]: ExternalDocumentationObjectSchemaDefinition,
  [REF_DEFINITIONS.InfoObject]: InfoObjectSchemaDefinition,
  [REF_DEFINITIONS.ContactObject]: ContactObjectSchemaDefinition,
  [REF_DEFINITIONS.LicenseObject]: LicenseObjectSchemaDefinition,
  [REF_DEFINITIONS.ResponseObject]: ResponseObjectSchemaDefinition,
  [REF_DEFINITIONS.ResponsesObject]: ResponsesObjectSchemaDefinition,
  [REF_DEFINITIONS.ParameterObject]: ParameterObjectSchemaDefinition,
  [REF_DEFINITIONS.ExampleObject]: ExampleObjectSchemaDefinition,
  [REF_DEFINITIONS.RequestBodyObject]: RequestBodyObjectSchemaDefinition,
  [REF_DEFINITIONS.SecuritySchemeObject]: SecuritySchemeObjectSchemaDefinition,
  [REF_DEFINITIONS.LinkObject]: LinkObjectSchemaDefinition,
  [REF_DEFINITIONS.XMLObject]: XMLObjectSchemaDefinition,
  [REF_DEFINITIONS.DiscriminatorObject]: DiscriminatorObjectSchemaDefinition,
  [REF_DEFINITIONS.OAuthFlowsObject]: OAuthFlowsObjectSchemaDefinition,
  [REF_DEFINITIONS.ServerVariableObject]: ServerVariableObjectSchemaDefinition,
  OpenApiDocument: OpenApiDocumentSchemaDefinition
  // Enforces that all references are included in the module
});
const OpenAPIDocumentSchema = module.Import("OpenApiDocument");
const ComponentsObjectSchema = module.Import("ComponentsObject");
const SecurityRequirementObjectSchema = module.Import("SecurityRequirementObject");
const TagObjectSchema = module.Import("TagObject");
const CallbackObjectSchema = module.Import("CallbackObject");
const PathItemObjectSchema = module.Import("PathItemObject");
const PathsObjectSchema = module.Import("PathsObject");
const OperationObjectSchema = module.Import("OperationObject");
const SchemaObjectSchema = module.Import("SchemaObject");
const EncodingObjectSchema = module.Import("EncodingObject");
const MediaTypeObjectSchema = module.Import("MediaTypeObject");
const HeaderObjectSchema = module.Import("HeaderObject");
const ServerObjectSchema = module.Import("ServerObject");
const ExternalDocumentationObjectSchema = module.Import("ExternalDocumentationObject");
const InfoObjectSchema = module.Import("InfoObject");
const ContactObjectSchema = module.Import("ContactObject");
const LicenseObjectSchema = module.Import("LicenseObject");
const ResponseObjectSchema = module.Import("ResponseObject");
const ResponsesObjectSchema = module.Import("ResponsesObject");
const ParameterObjectSchema = module.Import("ParameterObject");
const ExampleObjectSchema = module.Import("ExampleObject");
const RequestBodyObjectSchema = module.Import("RequestBodyObject");
const SecuritySchemeObjectSchema = module.Import("SecuritySchemeObject");
const LinkObjectSchema = module.Import("LinkObject");
const XMLObjectSchema = module.Import("XMLObject");
const DiscriminatorObjectSchema = module.Import("DiscriminatorObject");
const OAuthFlowsObjectSchema = module.Import("OAuthFlowsObject");
const ServerVariableObjectSchema = module.Import("ServerVariableObject");
export {
  CallbackObjectSchema,
  ComponentsObjectSchema,
  ContactObjectSchema,
  DiscriminatorObjectSchema,
  EncodingObjectSchema,
  ExampleObjectSchema,
  ExternalDocumentationObjectSchema,
  HeaderObjectSchema,
  InfoObjectSchema,
  LicenseObjectSchema,
  LinkObjectSchema,
  MediaTypeObjectSchema,
  OAuthFlowsObjectSchema,
  OpenAPIDocumentSchema,
  OperationObjectSchema,
  ParameterObjectSchema,
  PathItemObjectSchema,
  PathsObjectSchema,
  RequestBodyObjectSchema,
  ResponseObjectSchema,
  ResponsesObjectSchema,
  SchemaObjectSchema,
  SecurityRequirementObjectSchema,
  SecuritySchemeObjectSchema,
  ServerObjectSchema,
  ServerVariableObjectSchema,
  TagObjectSchema,
  XMLObjectSchema
};
//# sourceMappingURL=openapi-document.js.map
